// src/components/dev/KitchenSinkPage.tsx
import React, { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Calendar } from "../ui/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose, // Import DialogClose
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  //DropdownMenuCheckboxItem,
  //DropdownMenuRadioGroup,
  //DropdownMenuRadioItem,
  DropdownMenuGroup,
  DropdownMenuShortcut,
  //DropdownMenuSub,
  //DropdownMenuSubContent,
  //DropdownMenuSubTrigger,
  //DropdownMenuPortal
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Progress } from "../ui/progress";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel
} from "../ui/select";
import { Separator } from "../ui/separator";
import { Skeleton } from "../ui/skeleton";
import { Switch } from "../ui/switch";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../hooks/use-toast"; // For Toasts
//import { Toaster } from "../ui/toaster"; // Toaster component itself
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

import {
  AlertCircle,
  Terminal,
  //ChevronDown,
  //CircleUser,
  PlusCircle,
  Settings,
  LogOut,
  User,
  //Users,
  CreditCard,
  Mail,
  //MessageSquare,
  Github,
  //LifeBuoy,
  //Cloud,
  //Keyboard,
  Calendar as CalendarIcon,
  //Smile
} from "lucide-react"; // Some icons for demonstration
import { Avatar } from '../ui/avatar';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-12">
    <h2 className="text-2xl font-semibold mb-6 pb-2 border-b">{title}</h2>
    <div className="space-y-6">{children}</div>
  </div>
);

const ComponentGroup: React.FC<{ title?: string; children: React.ReactNode; className?: string }> = ({ title, children, className }) => (
  <div className={`p-4 border rounded-md ${className}`}>
    {title && <h3 className="text-lg font-medium mb-4">{title}</h3>}
    <div className="flex flex-wrap items-start gap-4">
      {children}
    </div>
  </div>
);


export function KitchenSinkPage() {
  const { toast } = useToast();
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [progress, setProgress] = useState(13);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showStatusBar, setShowStatusBar] = useState(true);
  const [showActivityBar, setShowActivityBar] = useState(false);
  const [showPanel, setShowPanel] = useState(false);


  React.useEffect(() => {
    const timer = setTimeout(() => setProgress(66), 500)
    return () => clearTimeout(timer)
  }, [])

  return (
    <TooltipProvider> {/* Needed for Tooltips to work */}
      <div className="container mx-auto p-4 md:p-8">
        <h1 className="text-4xl font-bold mb-10 text-center">Component Kitchen Sink</h1>

        {/* Buttons Section */}
        <Section title="Buttons">
          <ComponentGroup title="Variants">
            <Button>Default (Primary)</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="destructive">Destructive</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="link">Link</Button>
          </ComponentGroup>
          <ComponentGroup title="Sizes">
            <Button size="sm">Small</Button>
            <Button size="default">Default Size</Button>
            <Button size="lg">Large</Button>
            <Button size="icon"><Mail className="h-4 w-4" /></Button>
          </ComponentGroup>
          <ComponentGroup title="With Icons">
            <Button><Mail className="mr-2 h-4 w-4" /> Login with Email</Button>
            <Button variant="outline"><Github className="mr-2 h-4 w-4" /> GitHub</Button>
          </ComponentGroup>
          <ComponentGroup title="States">
            <Button disabled>Disabled</Button>
            <Button>Loading... (manual state)</Button> {/* You'd handle loading state logic */}
          </ComponentGroup>
        </Section>

        {/* Cards Section */}
        <Section title="Cards">
          <Card className="w-full max-w-sm">
            <CardHeader>
              <CardTitle>Lead Title/Name</CardTitle>
              <CardDescription>Brief description of the lead or category.</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Key information about the lead like location, industry, or potential value.</p>
              <div className="flex space-x-2 mt-4">
                <Badge>New</Badge>
                <Badge variant="secondary">Verified</Badge>
                <Badge variant="destructive">Urgent</Badge>
                <Badge variant="outline">Technology</Badge>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline">View Details</Button>
              <Button>Buy Lead</Button>
            </CardFooter>
          </Card>
        </Section>
        
        {/* Badges & Avatar Section */}
        <Section title="Badges & Avatar">
          <ComponentGroup title="Badges">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="destructive">Destructive</Badge>
            <Badge variant="outline">Outline</Badge>
          </ComponentGroup>
          <ComponentGroup title="Avatar">
            <Avatar>
              <AvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
              <AvatarFallback>CN</AvatarFallback>
            </Avatar>
            <Avatar>
              <AvatarFallback>JD</AvatarFallback>
            </Avatar>
          </ComponentGroup>
        </Section>

        {/* Forms Section */}
        <Section title="Forms & Inputs">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ComponentGroup title="Input & Label" className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input type="email" id="email" placeholder="you@example.com" />
              </div>
              <div>
                <Label htmlFor="name_disabled">Name (Disabled)</Label>
                <Input type="text" id="name_disabled" placeholder="Your Name" disabled />
              </div>
            </ComponentGroup>

            <ComponentGroup title="Textarea" className="space-y-2">
              <Label htmlFor="message">Your Message</Label>
              <Textarea placeholder="Type your message here." id="message" />
            </ComponentGroup>
            
            <ComponentGroup title="Checkbox & Switch" className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox id="terms" />
                <Label htmlFor="terms">Accept terms and conditions</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch id="notifications-switch" />
                <Label htmlFor="notifications-switch">Enable Notifications</Label>
              </div>
            </ComponentGroup>

            <ComponentGroup title="Radio Group">
              <RadioGroup defaultValue="option-one">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="option-one" id="option-one" />
                  <Label htmlFor="option-one">Option One</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="option-two" id="option-two" />
                  <Label htmlFor="option-two">Option Two</Label>
                </div>
              </RadioGroup>
            </ComponentGroup>

            <ComponentGroup title="Select (Dropdown)">
              <Select>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select a fruit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Fruits</SelectLabel>
                    <SelectItem value="apple">Apple</SelectItem>
                    <SelectItem value="banana">Banana</SelectItem>
                    <SelectItem value="blueberry">Blueberry</SelectItem>
                    <SelectItem value="grapes">Grapes</SelectItem>
                    <SelectItem value="pineapple">Pineapple</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </ComponentGroup>

            <ComponentGroup title="Date Picker (Calendar & Popover)">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant={"outline"}
                    className="w-[280px] justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? new Intl.DateTimeFormat('en-US').format(date) : <span>Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </ComponentGroup>
          </div>
        </Section>

        {/* Feedback & Overlays Section */}
        <Section title="Feedback & Overlays">
          <ComponentGroup title="Alerts">
            <Alert>
              <Terminal className="h-4 w-4" />
              <AlertTitle>Heads up!</AlertTitle>
              <AlertDescription>
                You can add components to your app using the CLI.
              </AlertDescription>
            </Alert>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                Your session has expired. Please log in again.
              </AlertDescription>
            </Alert>
          </ComponentGroup>
          
          <ComponentGroup title="Dialog (Modal)">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">Open Dialog</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Edit profile</DialogTitle>
                  <DialogDescription>
                    Make changes to your profile here. Click save when you're done.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  {/* Form content can go here */}
                  <p>Dialog content goes here...</p>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="secondary">Close</Button>
                  </DialogClose>
                  <Button type="submit">Save changes</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </ComponentGroup>

          <ComponentGroup title="Alert Dialog">
             <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="destructive">Show Alert Dialog</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. This will permanently delete your
                    account and remove your data from our servers.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => console.log("Delete confirmed")}>Continue</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </ComponentGroup>

          <ComponentGroup title="Toast">
            <Button
              variant="outline"
              onClick={() => {
                toast({
                  title: "Scheduled: Catch up ",
                  description: "Friday, February 10, 2023 at 5:57 PM",
                  action: (
                    <Button variant="ghost" size="sm" onClick={() => console.log('Undo clicked')}>Undo</Button>
                  ),
                })
              }}
            >
              Show Toast
            </Button>
             <Button
              variant="destructive"
              onClick={() => {
                toast({
                  variant: "destructive",
                  title: "Uh oh! Something went wrong.",
                  description: "There was a problem with your request.",
                   action: (
                    <Button variant="ghost" size="sm" onClick={() => console.log('Try again clicked')}>Try again</Button>
                  ),
                })
              }}
            >
              Show Destructive Toast
            </Button>
          </ComponentGroup>

          <ComponentGroup title="Tooltip">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" className="w-10 rounded-full p-0">
                  <PlusCircle className="h-4 w-4" />
                  <span className="sr-only">Add to library</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Add to library</p>
              </TooltipContent>
            </Tooltip>
          </ComponentGroup>
        </Section>

        {/* Navigation Section */}
         <Section title="Navigation">
          <ComponentGroup title="Dropdown Menu">
             <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">Open User Menu</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                    <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <CreditCard className="mr-2 h-4 w-4" />
                    <span>Billing</span>
                    <DropdownMenuShortcut>⌘B</DropdownMenuShortcut>
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                    <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => console.log('Logged out')}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Log out</span>
                  <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </ComponentGroup>
          
          <ComponentGroup title="Tabs">
            <Tabs defaultValue="account" className="w-[400px]">
              <TabsList>
                <TabsTrigger value="account">Account</TabsTrigger>
                <TabsTrigger value="password">Password</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
              </TabsList>
              <TabsContent value="account" className="p-4 border rounded-md mt-2">
                Make changes to your account here.
              </TabsContent>
              <TabsContent value="password" className="p-4 border rounded-md mt-2">
                Change your password here.
              </TabsContent>
               <TabsContent value="notifications" className="p-4 border rounded-md mt-2">
                Manage your notification settings.
              </TabsContent>
            </Tabs>
          </ComponentGroup>
        </Section>

        {/* Other Components Section */}
        <Section title="Other Components">
          <ComponentGroup title="Accordion">
            <Accordion type="single" collapsible className="w-full max-w-md">
              <AccordionItem value="item-1">
                <AccordionTrigger>Is it accessible?</AccordionTrigger>
                <AccordionContent>
                  Yes. It adheres to the WAI-ARIA design pattern.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>Is it styled?</AccordionTrigger>
                <AccordionContent>
                  Yes. It comes with default styles that matches the other
                  components&apos; aesthetic.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </ComponentGroup>

          <ComponentGroup title="Progress Bar">
            <Progress value={progress} className="w-[60%]" />
            <p className="text-sm text-muted-foreground">Current progress: {progress}%</p>
          </ComponentGroup>
          
          <ComponentGroup title="Separator">
            <div className="space-y-1">
              <h4 className="text-sm font-medium leading-none">Radix Primitives</h4>
              <p className="text-sm text-muted-foreground">
                An open-source UI component library.
              </p>
            </div>
            <Separator className="my-4" />
            <div className="flex h-5 items-center space-x-4 text-sm">
              <div>Blog</div>
              <Separator orientation="vertical" />
              <div>Docs</div>
              <Separator orientation="vertical" />
              <div>Source</div>
            </div>
          </ComponentGroup>
          
          <ComponentGroup title="Skeleton Loaders">
            <div className="flex items-center space-x-4">
              <Skeleton className="h-12 w-12 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-[250px]" />
                <Skeleton className="h-4 w-[200px]" />
              </div>
            </div>
             <Skeleton className="h-[125px] w-full rounded-xl mt-4" />
          </ComponentGroup>

          <ComponentGroup title="Table">
            <Table>
              <TableCaption>A list of your recent invoices.</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Invoice</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">INV001</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Credit Card</TableCell>
                  <TableCell className="text-right">$250.00</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">INV002</TableCell>
                  <TableCell>Pending</TableCell>
                  <TableCell>PayPal</TableCell>
                  <TableCell className="text-right">$150.00</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </ComponentGroup>
        </Section>

      </div>
    </TooltipProvider>
  );
}

// Don't forget to add <Toaster /> to your main App layout (e.g., App.tsx or Layout.tsx)
// For example, in your App.tsx:
// import { Toaster } from "../ui/toaster"
// function App() {
//   return (
//     <>
//       {/* ... your app content ... */}
//       <Toaster />
//     </>
//   )
// }